import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import { buildTrace, TraceMessage, TraceRow } from '../Components/AgentAuditOperationDetailHelpers';
import AvailableTools from '../Components/AvailableTools';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import Table from '../Components/Tables';
import {
  SimulationDatasetApiProvider,
  useSimulationDatasetApi,
} from '../Services/SimulationDatasetApiProvider';
import { EvalScanDetail } from '../Services/SimulationDatasetApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';
import { stripHtmlAndTruncate } from '../utils/stripHtmlAndTruncate';
import './SimulationDatasetConversation.css';

function statusLabel(scan: EvalScanDetail): string {
  if (scan.status === 'ERROR') return 'Error';
  if (scan.status !== 'DONE') return scan.status;
  return scan.passed ? 'Passed' : 'Failed';
}

function SimulationDatasetScanDetailContent() {
  const { organizationId, datasetKey, runKey, scanKey } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getScanDetail } = useSimulationDatasetApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [scan, setScan] = useState<EvalScanDetail | null>(null);

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!datasetKey || !scanKey) return;
      try {
        setLoading(true);
        const data = await getScanDetail(datasetKey, scanKey);
        setScan(data);
      } catch (error) {
        console.error('Error loading test result:', error);
        showAlert('fail', 'Failed to load test result');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [datasetKey, scanKey]);

  const onBack = (): void => {
    navigate(
      `/organizations/${organizationId}/simulation-datasets/${datasetKey}/evaluations/${runKey}`,
    );
  };

  if (loading) return <PageLoader />;
  if (!scan) return null;

  const traceColumns = [
    { name: 'Type', selector: (row: TraceRow) => row.type, sortable: false, width: '120px' },
    {
      name: 'Content',
      selector: (row: TraceRow) => stripHtmlAndTruncate(row.description),
      sortable: false,
    },
  ];

  const traceRows = buildTrace((scan.trace_messages as TraceMessage[]) || []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center" style={{ marginBottom: 12 }}>
            <h2 className="h2-title" style={{ margin: 0 }}>
              {scan.conversation_name || 'Test'} — result
            </h2>
            <Button variant="ghost" size="medium" onClick={onBack}>
              Back
            </Button>
          </div>

          <div className="gap d-flex flex-column">
            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Overview</h4>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Status</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <p>
                    {statusLabel(scan)}
                    {scan.score !== null ? ` · Score: ${scan.score}` : ''}
                  </p>
                </div>
              </div>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Duration</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <p>{scan.duration_ms !== null ? `${scan.duration_ms} ms` : '—'}</p>
                </div>
              </div>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Created At</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <p>{formatDateUTC(scan.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Conversation trace</h4>
              <AvailableTools tools={scan.available_tools} />
              <Table
                title=""
                columns={traceColumns}
                data={traceRows}
                expandableRows={true}
                dense={false}
                noTableHead={false}
                pagination={false}
              />
            </div>

            {scan.error && (
              <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
                <h4 className="h4-title">Error</h4>
                <div className="sim-scan-field-value sim-scan-error">{scan.error}</div>
              </div>
            )}

            {scan.expected && (
              <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
                <h4 className="h4-title">Expected</h4>
                <div className="sim-scan-field-value">{scan.expected}</div>
              </div>
            )}

            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Evaluation</h4>
              {scan.result?.reasoning && (
                <div className="sim-scan-field">
                  <div className="sim-scan-field-label">Reasoning</div>
                  <div className="sim-scan-field-value">{scan.result.reasoning}</div>
                </div>
              )}
              {scan.result?.key_matches && scan.result.key_matches.length > 0 && (
                <div className="sim-scan-field">
                  <div className="sim-scan-field-label">What matched</div>
                  <ul className="sim-points sim-points-match">
                    {scan.result.key_matches.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
              {scan.result?.key_misses && scan.result.key_misses.length > 0 && (
                <div className="sim-scan-field">
                  <div className="sim-scan-field-label">What was missing</div>
                  <ul className="sim-points sim-points-miss">
                    {scan.result.key_misses.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!scan.result && <div className="sim-chat-empty">No evaluation result yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SimulationDatasetScanDetail() {
  return (
    <ConfirmationProvider>
      <SimulationDatasetApiProvider>
        <SimulationDatasetScanDetailContent />
      </SimulationDatasetApiProvider>
    </ConfirmationProvider>
  );
}

export default SimulationDatasetScanDetail;
